import type { ReportVersion } from "./domain";

/** Source observations are deliberately separate from assessment and client wording. */
export type HostedSourceReference = {
  schemaVersion: 1;
  id: string;
  revision: number;
  kind: "url" | "pasted_text" | "operator_observation" | "private_file" | "ayzeo_pdf_snapshot" | "evidence_record";
  label: string;
  observedAt: string;
  addedAt: string;
  addedByUserId: string;
  addedByLabel: string;
  visibility: "Internal only" | "Client visible";
  url?: string;
  text?: string;
  observation?: string;
  sourceId?: string;
};

const durableKinds = new Set<HostedSourceReference["kind"]>(["private_file", "ayzeo_pdf_snapshot", "evidence_record"]);

export function canonicalEvidenceUrl(input: string): string {
  const url = new URL(input.trim());
  if (url.protocol !== "https:" && url.protocol !== "http:") throw new Error("Source URL must be HTTP or HTTPS.");
  if (url.username || url.password) throw new Error("Source URL cannot contain credentials.");
  return url.href;
}

export function validateHostedSourceReference(reference: HostedSourceReference): void {
  if (reference.schemaVersion !== 1 || !reference.id.trim() || !Number.isSafeInteger(reference.revision) || reference.revision < 1 || !reference.label.trim() || !reference.addedByUserId.trim() || !reference.addedAt || !reference.observedAt) throw new Error("Source identity, provenance and observation date are required.");
  if (!Number.isFinite(Date.parse(reference.addedAt)) || !Number.isFinite(Date.parse(reference.observedAt))) throw new Error("Source provenance dates are invalid.");
  if (reference.visibility !== "Internal only" && reference.visibility !== "Client visible") throw new Error("Source visibility is invalid.");
  if (!(["url", "pasted_text", "operator_observation", "private_file", "ayzeo_pdf_snapshot", "evidence_record"] as string[]).includes(reference.kind)) throw new Error("Source kind is unsupported.");
  if (reference.url) canonicalEvidenceUrl(reference.url);
  if (durableKinds.has(reference.kind)) {
    if (!reference.sourceId?.trim() || /^(?:blob:|file:|data:|[A-Za-z]:\\)/u.test(reference.sourceId) || reference.url || reference.text || reference.observation) throw new Error("A durable source requires only its stored source ID.");
  } else if (reference.sourceId) throw new Error("A manual source cannot claim a durable source ID.");
  if (reference.kind === "url" && !reference.url) throw new Error("URL evidence requires a source URL.");
  if (reference.kind === "pasted_text" && !reference.text?.trim()) throw new Error("Pasted evidence requires supplied text.");
  if (reference.kind === "operator_observation" && !reference.observation?.trim()) throw new Error("Operator evidence requires an observation.");
}

export function replaceHostedSourceReference(existing: HostedSourceReference[], next: HostedSourceReference): HostedSourceReference[] {
  validateHostedSourceReference(next);
  const previous = existing.find(item => item.id === next.id);
  if (previous && (next.revision !== previous.revision + 1 || next.addedByUserId !== previous.addedByUserId || next.addedAt !== previous.addedAt)) throw new Error("Source revision or original provenance changed.");
  return previous ? existing.map(item => item.id === next.id ? next : item) : [...existing, next];
}

export function removeHostedSourceReference(existing: HostedSourceReference[], id: string): HostedSourceReference[] {
  return existing.filter(item => item.id !== id);
}

/** Stable change signal for a specialist assessment's exact set of source inputs. */
export function hostedSourceFingerprint(references: HostedSourceReference[]): string {
  return JSON.stringify([...references].sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** A confirmed specialist assessment is current only for the exact source set and working revision it reviewed. */
export function hostedAssessmentIsCurrent(
  references: HostedSourceReference[],
  confirmed: { editedAt: string; hostedSourceFingerprint?: string } | undefined,
  working?: { editedAt: string },
): boolean {
  return Boolean(confirmed && confirmed.hostedSourceFingerprint === hostedSourceFingerprint(references) &&
    (!working || working.editedAt <= confirmed.editedAt));
}

export function newHostedSourceReference(
  input: Omit<HostedSourceReference, "schemaVersion" | "id" | "revision" | "addedAt" | "addedByUserId" | "addedByLabel">,
  actor: { id: string; label: string },
  now = new Date().toISOString(),
): HostedSourceReference {
  if (!actor.id || !actor.label) throw new Error("An authenticated operator is required for source provenance.");
  const reference: HostedSourceReference = { ...input, schemaVersion: 1, id: `source-${crypto.randomUUID()}`, revision: 1,
    addedAt: now, addedByUserId: actor.id, addedByLabel: actor.label,
    url: input.url ? canonicalEvidenceUrl(input.url) : undefined };
  validateHostedSourceReference(reference);
  return reference;
}

/** Batch 2B composes this pure edit with the existing hosted save-report-version queue. */
export function withHostedSourceReference(version: ReportVersion, sectionId: string, reference: HostedSourceReference): ReportVersion {
  if (version.status !== "Draft") throw new Error("Only a Draft report can change source references.");
  if (!version.sections.some(section => section.id === sectionId)) throw new Error("The report section is unavailable.");
  return { ...version, sections: version.sections.map(section => section.id !== sectionId ? section :
    { ...section, hostedSourceReferences: replaceHostedSourceReference(section.hostedSourceReferences ?? [], reference) }) };
}

export function withoutHostedSourceReference(version: ReportVersion, sectionId: string, referenceId: string): ReportVersion {
  if (version.status !== "Draft") throw new Error("Only a Draft report can change source references.");
  if (!version.sections.some(section => section.id === sectionId)) throw new Error("The report section is unavailable.");
  return { ...version, sections: version.sections.map(section => section.id !== sectionId ? section :
    { ...section, hostedSourceReferences: removeHostedSourceReference(section.hostedSourceReferences ?? [], referenceId) }) };
}
